import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { type ResolvedDateTimeFormat } from "@/shared/datetime";
import { activeDateTimeFormat } from "@/shared/datetime/formatters";
import { NOT_SET_LABEL } from "@/shared/domain/query/notSet";
import { formatBoolean } from "@/shared/text";

import { type Filter, isListComparator, isValueless } from "./FilterSchema";

dayjs.extend(utc);

const DEFAULT_VALUE_LIMIT = 2;

export interface FormatFilterValueOptions {
  /**
   * Max values rendered before collapsing into "+N more". Applies to every
   * multi-value type. Date BETWEEN (always two values by contract) is unaffected.
   */
  valueLimit?: number;
  /**
   * The format a date filter's values are written in, reading the viewer's
   * local zone (each value's calendar day is rebuilt as a local date).
   * Defaults to the active format; a component passes the one from
   * `useDateTimeFormat()` so the value follows the preference as it loads or
   * changes.
   */
  dateTimeFormat?: ResolvedDateTimeFormat;
}

/**
 * The visible parts of a chip's value segment. The chip renders `text` and,
 * if present, a softer "+N more" tail. The fields are kept separate so the
 * tail can be styled distinctly without the helper returning React nodes.
 */
export interface FormattedFilterValue {
  /** Primary visible text — e.g. `"foo, bar"`, `"3/3/2025 → 3/5/2025"`. */
  text: string;
  /**
   * Count of remaining multi-values collapsed past `valueLimit`. Omitted
   * when nothing overflows or for non-multi-value types.
   */
  more?: number;
}

/**
 * Render the value-segment payload for a chip in `DataTable.ActiveFilters`.
 * Returns `null` when the comparator carries the full semantic on its own
 * (IS_NULL / IS_NOT_NULL / date presets), and for an enum or boolean list of
 * "Not set" alone, whose chip reads "is not set" / "is set" (see
 * `comparatorLabelForFilter`) — callers should omit the value segment
 * entirely in that case.
 *
 * Inputs are assumed valid per `filterSchema`'s refine — empty arrays for
 * non-valueless comparators, malformed BETWEEN ranges, and NaN/Infinity
 * for numerics are rejected upstream by the per-type value inputs and the
 * schema refine. This helper does not re-validate.
 */
export function formatFilterValue(
  filter: Filter,
  opts: FormatFilterValueOptions = {},
): FormattedFilterValue | null {
  if (isValueless(filter.comparator) || isNotSetOnlyChecklist(filter)) {
    return null;
  }
  const limit = opts.valueLimit ?? DEFAULT_VALUE_LIMIT;

  switch (filter.type) {
    case "string":
      return joinWithLimit(withNotSet(filter, filter.values), limit);
    // `metric` formats identically to `numeric` — stringify each number.
    case "numeric":
    case "metric":
      return joinWithLimit(
        withNotSet(filter, filter.values.map(String)),
        limit,
      );
    // An identity chip renders exactly like an enum: each selection carries
    // the friendly name the directory showed when it was picked. The
    // whole-kind match never reaches here — `IS_ANY_<KIND>` is valueless, so
    // the comparator label carries it and this returns `null` above.
    case "set":
    case "enum":
    case "identity":
      // Option-backed filters store labeled options, so the chip renders each
      // selection's own captured label — no option-list lookup needed.
      return joinWithLimit(
        withNotSet(
          filter,
          filter.values.map((option) => option.label),
        ),
        limit,
      );
    case "boolean":
      return joinWithLimit(
        withNotSet(filter, filter.values.map(formatBoolean)),
        limit,
      );
    case "date": {
      const dateTimeFormat = opts.dateTimeFormat ?? activeDateTimeFormat();
      if (filter.comparator === "BETWEEN") {
        return {
          text: `${formatDate(filter.values[0], dateTimeFormat)} → ${formatDate(filter.values[1], dateTimeFormat)}`,
        };
      }
      return { text: formatDate(filter.values[0], dateTimeFormat) };
    }
  }
}

/** True for an enum or boolean list whose only member is "Not set". */
function isNotSetOnlyChecklist(filter: Filter): boolean {
  return (
    (filter.type === "enum" || filter.type === "boolean") &&
    isListComparator(filter.comparator) &&
    filter.includeNull === true &&
    filter.values.length === 0
  );
}

/**
 * A list filter's members with "Not set" leading when the filter includes it:
 * it is the one member the user did not type or pick from the values, and
 * listing it first keeps it visible past the "+N more" cut.
 */
function withNotSet(filter: Filter, parts: string[]): string[] {
  return "includeNull" in filter &&
    filter.includeNull === true &&
    isListComparator(filter.comparator)
    ? [NOT_SET_LABEL, ...parts]
    : parts;
}

/**
 * Several values on one line: the first `limit` joined by commas, with how
 * many more there are, as a filter chip shows a list filter's values.
 */
export function joinWithLimit(
  parts: readonly string[],
  limit = DEFAULT_VALUE_LIMIT,
): FormattedFilterValue {
  if (parts.length > limit) {
    return {
      text: parts.slice(0, limit).join(", "),
      more: parts.length - limit,
    };
  }
  return { text: parts.join(", ") };
}

// Dates are stored as ISO 8601 UTC-midnight strings (the date input's
// convention). Read the calendar day in UTC (so it stays stable regardless
// of the viewer's local timezone), then rebuild it as a local Date carrying
// that same Y/M/D so `dateTimeFormat` (e.g. Swiss DD.MM.YYYY) writes it
// without a timezone-induced day shift.
function formatDate(
  iso: string,
  dateTimeFormat: ResolvedDateTimeFormat,
): string {
  const utcDay = dayjs.utc(iso);
  if (!utcDay.isValid()) {
    return iso;
  }
  return dateTimeFormat.format(
    new Date(utcDay.year(), utcDay.month(), utcDay.date()),
    { time: "none" },
  );
}
